import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  dispatcherApi,
  type DeliveriesListResponse,
  type DeliveryDetailResponse,
  type LiveCouriersResponse,
} from "@/lib/admin-api/dispatcher";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  deliveries: (filters: DispatchFilters) =>
    ["dispatcher-deliveries", filters] as const,
  detail: (ref: string | undefined) => ["dispatcher-detail", ref] as const,
  couriersLive: ["dispatcher-couriers-live"] as const,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DispatchFilters {
  readonly status?: string;
  readonly exceptions?: boolean;
  readonly date?: string;
  readonly page?: number;
}

// ---------------------------------------------------------------------------
// useDispatches — paginated/filtered delivery list with realtime invalidation
// ---------------------------------------------------------------------------

export function useDispatches(filters: DispatchFilters = {}) {
  const queryClient = useQueryClient();

  // Subscribe to Supabase realtime on the dispatcher-board channel.
  // Any INSERT / UPDATE / DELETE on the channel triggers a fresh fetch so
  // the table stays live without full polling.
  useEffect(() => {
    const channel = supabase
      .channel("dispatcher-board")
      .on("broadcast", { event: "*" }, () => {
        void queryClient.invalidateQueries({ queryKey: ["dispatcher-deliveries"] });
      })
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "deliveries" },
        () => {
          void queryClient.invalidateQueries({ queryKey: ["dispatcher-deliveries"] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return useQuery<DeliveriesListResponse>({
    queryKey: KEYS.deliveries(filters),
    queryFn: () => dispatcherApi.deliveries.list(filters),
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------------------
// useDispatchDetail — single delivery by ref (e.g. "YBK-0001")
// ---------------------------------------------------------------------------

export function useDispatchDetail(ref: string | undefined) {
  return useQuery<DeliveryDetailResponse>({
    queryKey: KEYS.detail(ref),
    queryFn: () => dispatcherApi.deliveries.get(ref!),
    enabled: Boolean(ref),
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// useLiveCouriers — live courier positions, polled every 10 s
// ---------------------------------------------------------------------------

export function useLiveCouriers() {
  return useQuery<LiveCouriersResponse>({
    queryKey: KEYS.couriersLive,
    queryFn: () => dispatcherApi.couriers.live(),
    refetchInterval: 10_000,
    staleTime: 0,
  });
}
